import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Building2,
  MapPin,
  Briefcase,
  CalendarDays,
  User,
  BadgeDollarSign,
  CalendarPlus,
  Link2,
  FileText,
  StickyNote,
  Pencil,
  Trash2,
  Download,
  Video,
  Mail,
  Eye,
  Archive,
  ArchiveRestore,
  Bot,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ApplicationDetail, ApplicationStatus, Interview, Resume } from '../../types';
import { applicationsService, getApiErrorMessage } from '../../services/api';
import { COLUMN_META, EMPLOYMENT_LABELS, INTERVIEW_STATUS_META, INTERVIEW_TYPE_LABELS } from './kanbanConstants';
import { formatDate, formatDateTime } from '../../utils/formatDate';
import ApplicationFormModal from './ApplicationFormModal';
import InterviewFormModal from './InterviewFormModal';
import ResumePreviewModal from '../ResumePreviewModal';
import ResumeThumb from '../ResumeThumb';

interface Props {
  applicationId: number;
  resumes: Resume[];
  onClose: () => void;
  onBoardChanged: () => void;
}

const ApplicationDrawer: React.FC<Props> = ({ applicationId, resumes, onClose, onBoardChanged }) => {
  const navigate = useNavigate();
  const [application, setApplication] = useState<ApplicationDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [interviewModal, setInterviewModal] = useState<{ open: boolean; interview: Interview | null }>({ open: false, interview: null });
  const [busyInterviewId, setBusyInterviewId] = useState<number | null>(null);
  const [viewResume, setViewResume] = useState(false);

  const fetchDetail = async () => {
    setIsLoading(true);
    try {
      const res = await applicationsService.get(applicationId);
      setApplication(res.data);
    } catch {
      toast.error('Could not load application details.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  if (!application) {
    return null;
  }

  const meta = COLUMN_META[application.status];
  const sortedInterviews = [...application.interviews].sort(
    (a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
  );
  const linkedResume = application.resumeId ? resumes.find((r) => r.id === application.resumeId) ?? null : null;

  const handleStatusChange = async (status: ApplicationStatus) => {
    setIsChangingStatus(true);
    try {
      await applicationsService.updateStatus(application.id, status);
      setApplication((prev) => (prev ? { ...prev, status } : prev));
      onBoardChanged();
      toast.success(`Moved to ${COLUMN_META[status].label}.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not update status.'));
    } finally {
      setIsChangingStatus(false);
    }
  };

  const handleDelete = async () => {
    try {
      await applicationsService.remove(application.id);
      toast.success('Application deleted.');
      onBoardChanged();
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not delete application.'));
    }
  };

  const handleArchive = async (archive: boolean) => {
    try {
      await applicationsService.setArchived(application.id, archive);
      toast.success(archive ? 'Application archived.' : 'Application restored to the board.');
      onBoardChanged();
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, `Could not ${archive ? 'archive' : 'restore'} the application.`));
    }
  };

  const handleDeleteInterview = async (interview: Interview) => {
    setBusyInterviewId(interview.id);
    try {
      await applicationsService.deleteInterview(interview.id);
      toast.success('Interview deleted.');
      await fetchDetail();
      onBoardChanged();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not delete interview.'));
    } finally {
      setBusyInterviewId(null);
    }
  };

  const handleDownloadIcs = async (interview: Interview) => {
    setBusyInterviewId(interview.id);
    try {
      await applicationsService.downloadIcs(interview.id);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not download the calendar invite.'));
    } finally {
      setBusyInterviewId(null);
    }
  };

  const renderOverviewItem = (icon: React.ReactNode, label: string, value: React.ReactNode) => (
    <div className="flex items-start gap-2.5">
      <span className="text-zinc-400 mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0">
        <p className="text-[11px] text-zinc-400">{label}</p>
        <p className="text-[13px] text-foreground break-words">{value}</p>
      </div>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-[150] bg-black/40 backdrop-blur-sm" onClick={onClose}>
        <motion.aside
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
          className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-elevated flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
            <div className="flex items-center gap-3 min-w-0">
              {application.organizationLogo ? (
                <img
                  src={application.organizationLogo}
                  alt=""
                  className="w-10 h-10 rounded-btn object-cover border border-zinc-100 bg-white"
                />
              ) : (
                <div className="w-10 h-10 rounded-btn bg-brand-50 flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 text-brand-500" />
                </div>
              )}
              <div className="min-w-0">
                <h3 className="font-display font-semibold text-foreground truncate text-base">{application.jobTitle}</h3>
                <p className="text-xs text-zinc-500 truncate">{application.organizationName}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-zinc-400 hover:text-foreground transition-colors shrink-0 ml-2" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {isLoading ? (
            <div className="flex-1 p-6 space-y-4 animate-pulse">
              <div className="h-10 bg-zinc-100 rounded-btn" />
              <div className="h-24 bg-zinc-100 rounded-card" />
              <div className="h-40 bg-zinc-100 rounded-card" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-zinc-500">Status</label>
                <select
                  value={application.status}
                  onChange={(e) => handleStatusChange(e.target.value as ApplicationStatus)}
                  disabled={isChangingStatus}
                  className={`h-8 pl-2 pr-7 border text-xs font-semibold rounded-btn focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 ${meta.badge}`}
                >
                  {(Object.keys(COLUMN_META) as ApplicationStatus[]).map((s) => (
                    <option key={s} value={s}>{COLUMN_META[s].label}</option>
                  ))}
                </select>
                {application.archived && (
                  <span className="text-[10px] font-semibold text-zinc-500 bg-zinc-100 px-2 py-1 rounded inline-flex items-center gap-1">
                    <Archive className="w-3 h-3" /> Archived
                    {application.archivedAt ? ` ${formatDate(application.archivedAt)}` : ''}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-zinc-50 border border-zinc-200 rounded-card p-4">
                {renderOverviewItem(
                  <Briefcase className="w-4 h-4" />,
                  'Employment',
                  application.employmentType ? EMPLOYMENT_LABELS[application.employmentType] : '—'
                )}
                {renderOverviewItem(
                  <MapPin className="w-4 h-4" />,
                  'Location',
                  application.location || '—'
                )}
                {renderOverviewItem(
                  <CalendarDays className="w-4 h-4" />,
                  'Applied',
                  formatDate(application.appliedAt)
                )}
                {renderOverviewItem(
                  <BadgeDollarSign className="w-4 h-4" />,
                  'Salary',
                  application.salaryRange || '—'
                )}
                {renderOverviewItem(
                  <FileText className="w-4 h-4" />,
                  'Resume used',
                  application.resumeLabel
                )}
                {renderOverviewItem(
                  <Link2 className="w-4 h-4" />,
                  'Job posting',
                  application.jobUrl ? (
                    <a href={application.jobUrl} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">
                      Open posting
                    </a>
                  ) : (
                    '—'
                  )
                )}
              </div>

              <div className="border border-zinc-200 rounded-card p-4">
                <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide mb-3 inline-flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-zinc-400" /> Resume
                </h4>
                {linkedResume ? (
                  <div className="flex items-start gap-3.5">
                    <div className="relative w-24 shrink-0 aspect-[1/1.414] rounded-card border border-zinc-200 overflow-hidden bg-zinc-50">
                      <ResumeThumb resume={linkedResume} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground truncate">{linkedResume.personalInfo.fullName}</p>
                      <p className="text-xs text-zinc-400 mt-0.5 truncate">{linkedResume.templateId}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">Created {formatDate(linkedResume.createdAt)}</p>
                      <button
                        onClick={() => setViewResume(true)}
                        className="mt-2.5 h-8 px-3 inline-flex items-center gap-1.5 text-xs font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 rounded-btn transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" /> View full resume
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-zinc-400">
                    No resume attached to this application. Edit it to link a resume card.
                  </p>
                )}
              </div>

              {(application.recruiterName || application.recruiterEmail) && (
                <div className="border border-zinc-200 rounded-card p-4">
                  <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide mb-3 inline-flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-zinc-400" /> Recruiter
                  </h4>
                  <div className="space-y-1.5 text-sm">
                    {application.recruiterName && (
                      <p className="text-foreground">{application.recruiterName}</p>
                    )}
                    {application.recruiterEmail && (
                      <a href={`mailto:${application.recruiterEmail}`} className="inline-flex items-center gap-1.5 text-brand-600 hover:underline">
                        <Mail className="w-3.5 h-3.5" /> {application.recruiterEmail}
                      </a>
                    )}
                  </div>
                </div>
              )}

              {application.notes && (
                <div className="border border-zinc-200 rounded-card p-4">
                  <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide mb-2 inline-flex items-center gap-1.5">
                    <StickyNote className="w-3.5 h-3.5 text-zinc-400" /> Notes
                  </h4>
                  <p className="text-sm text-zinc-600 whitespace-pre-wrap">{application.notes}</p>
                </div>
              )}

              <div className="border border-zinc-200 rounded-card overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
                  <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide inline-flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5 text-zinc-400" /> Interviews ({sortedInterviews.length})
                  </h4>
                  <button
                    onClick={() => setInterviewModal({ open: true, interview: null })}
                    className="h-8 px-3 inline-flex items-center gap-1.5 text-xs font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 rounded-btn transition-colors"
                  >
                    <CalendarPlus className="w-3.5 h-3.5" /> Add
                  </button>
                </div>
                {sortedInterviews.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-zinc-400 text-center">
                    No interviews yet. Add one and a calendar invite (.ics) is generated.
                  </p>
                ) : (
                  <ul className="divide-y divide-zinc-100">
                    {sortedInterviews.map((interview) => {
                      const statusMeta = INTERVIEW_STATUS_META[interview.status];
                      return (
                        <li key={interview.id} className="px-4 py-3">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground flex flex-wrap items-center gap-2">
                                {interview.round}
                                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${statusMeta.badge}`}>
                                  {statusMeta.label}
                                </span>
                              </p>
                              <p className="text-xs text-zinc-500 mt-0.5">
                                {formatDateTime(interview.scheduledAt)}
                                {interview.durationMinutes ? ` · ${interview.durationMinutes} min` : ''}
                              </p>
                              {(interview.type || interview.interviewer) && (
                                <p className="text-xs text-zinc-400 mt-0.5 truncate">
                                  {interview.type ? `${INTERVIEW_TYPE_LABELS[interview.type] ?? interview.type}` : ''}
                                  {interview.interviewer ? ` · ${interview.interviewer}` : ''}
                                </p>
                              )}
                              {interview.meetingLink && (
                                <a
                                  href={interview.meetingLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-brand-600 hover:underline mt-0.5"
                                >
                                  <Video className="w-3 h-3" /> Join meeting
                                </a>
                              )}
                              {interview.notes && (
                                <p className="text-xs text-zinc-500 bg-zinc-50 rounded-btn px-2 py-1 mt-1.5 whitespace-pre-wrap">
                                  {interview.notes}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
<button
                                  onClick={() => handleDownloadIcs(interview)}
                                  disabled={busyInterviewId === interview.id}
                                  className="w-7 h-7 inline-flex items-center justify-center text-zinc-400 hover:text-brand-600 disabled:opacity-40 transition-colors"
                                  title="Download calendar invite (.ics)"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                </button>
                              <button
                                onClick={() => setInterviewModal({ open: true, interview })}
                                className="w-7 h-7 inline-flex items-center justify-center text-zinc-400 hover:text-brand-600 transition-colors"
                                title="Edit interview"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteInterview(interview)}
                                disabled={busyInterviewId === interview.id}
                                className="w-7 h-7 inline-flex items-center justify-center text-zinc-400 hover:text-red-500 disabled:opacity-40 transition-colors"
                                title="Delete interview"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}

          <div className="px-5 py-4 border-t border-zinc-100 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => navigate(`/applications/${application.id}/ai-interview`)}
              className="h-10 px-4 inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-btn transition-colors"
            >
              <Bot className="w-4 h-4" /> AI Interview
            </button>
            <button
              onClick={() => handleArchive(!application.archived)}
              className="h-10 px-4 inline-flex items-center gap-1.5 text-sm font-medium text-zinc-600 border border-zinc-200 hover:bg-zinc-50 rounded-btn transition-colors"
            >
              {application.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
              {application.archived ? 'Restore' : 'Archive'}
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              className="h-10 px-4 inline-flex items-center gap-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-btn transition-colors"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
            <button
              onClick={() => setEditOpen(true)}
              className="h-10 px-4 ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-foreground border border-zinc-200 hover:bg-zinc-50 rounded-btn transition-colors"
            >
              <Pencil className="w-4 h-4" /> Edit
            </button>
          </div>

          {confirmDelete && (
            <div className="absolute inset-0 z-10 bg-white/80 backdrop-blur-sm flex items-center justify-center p-6">
              <div className="bg-white border border-zinc-200 rounded-card shadow-elevated p-6 max-w-sm w-full text-center">
                <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Trash2 className="w-5 h-5 text-red-500" />
                </div>
                <h4 className="font-display font-semibold text-foreground mb-1">Delete this application?</h4>
                <p className="text-sm text-zinc-500 mb-5">
                  "{application.jobTitle}" at {application.organizationName} and its interviews will be permanently removed.
                </p>
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="h-10 px-4 border border-zinc-200 hover:bg-zinc-50 text-zinc-600 text-sm font-medium rounded-btn transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="h-10 px-4 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-btn transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.aside>
      </div>

      {editOpen && (
        <ApplicationFormModal
          resumes={resumes}
          application={application}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => {
            setApplication(updated);
            onBoardChanged();
          }}
        />
      )}

      {interviewModal.open && (
        <InterviewFormModal
          applicationId={application.id}
          interview={interviewModal.interview}
          onClose={() => setInterviewModal({ open: false, interview: null })}
          onSaved={async () => {
            await fetchDetail();
            onBoardChanged();
          }}
        />
      )}

      {viewResume && linkedResume && (
        <ResumePreviewModal resume={linkedResume} onClose={() => setViewResume(false)} className="z-[200]" />
      )}
    </>
  );
};

export default ApplicationDrawer;